"use client";

import { useMemo } from "react";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import ListWithShowMore, {
  STOCK_LISTING_PAGINATION_BUTTON_CLASS,
} from "../../components/News/components/List";
import type { IStockItem } from "../../interfaces/StockList.interface";
import useGlobalIndicesListing from "./hooks/useGlobalIndicesListing";
import type { GlobalIndicesInitialTabData } from "./hooks/useGlobalIndicesListing";
import { selectComponent } from "../utility";

interface StockListWithTabsProps {
  data?: any;
  initialData?: IStockItem[];
  initialTabData?: GlobalIndicesInitialTabData;
}

function normalizeTabs(rawTabs: any): { label: string; value: string }[] {
  if (!Array.isArray(rawTabs) || rawTabs.length === 0) {
    return [];
  }

  if (
    rawTabs.every(
      (tab) => tab && typeof tab === "object" && "label" in tab && "value" in tab,
    )
  ) {
    return rawTabs as { label: string; value: string }[];
  }

  const first = rawTabs[0];
  if (first && typeof first === "object") {
    return Object.values(first)
      .filter((item: any) => item && typeof item === "object")
      .map((item: any) => ({
        label: String(item.label ?? ""),
        value: String(item.value ?? item.id ?? ""),
      }))
      .filter((tab) => tab.label && tab.value);
  }

  return [];
}

export default function StockListWithTabs({
  data,
  initialData = [],
  initialTabData,
}: Readonly<StockListWithTabsProps>) {
  const tabs = useMemo(() => normalizeTabs(data?.tabs), [data?.tabs]);
  const defaultTab = data?.defaultTab ?? tabs[0]?.value ?? "";

  const {
    selectedTab,
    setSelectedTab,
    rows,
    isLoading,
    isLoadingMore,
    showMore,
    handleShowMore,
  } = useGlobalIndicesListing({
    initialRows: initialData,
    initialTabData,
    defaultTab,
    limit: 10,
  });

  const tableRendererConfig = data?.renderComponent ?? {};
  const tableRendererProps = tableRendererConfig?.props ?? {};
  const tableComponentName =
    tableRendererConfig?.componentName ?? "globalIndicesStockTable";
  const tableComponentData = {
    ...tableRendererProps,
    data: rows,
    entityType: selectedTab,
    isLoading: isLoading || isLoadingMore,
    isListUpdating: isLoading && rows.length > 0,
    showBookmark: tableRendererProps.showBookmark ?? false,
    isGlobalIndicesPage: true,
    uiLabels: {
      stockListingAriaLabel: "Global indices listing",
      addToWatchlistAriaLabel: "Add {symbol} to watchlist",
      ...(tableRendererProps.uiLabels ?? {}),
    },
    tableLabels: {
      companyName: "Company name",
      ltp: "LTP",
      ...(tableRendererProps.tableLabels ?? {}),
    },
  };
  const tableComponentFn = selectComponent({
    componentName: tableComponentName,
    data: tableComponentData,
    index: 0,
    isMobileApp: false,
  });

  return (
    <>
      <BFLTabs
        tabs={tabs}
        selectedTab={selectedTab}
        onTabChange={(value: any) => setSelectedTab(String(value))}
        color={data?.color}
        gapClassName="gap-4"
        className="pl-4"
        paragraphClassName="mb-[11px] font-normal"
        indicatorClassName="bottom-0 md:bottom-0 bg-[#ff6700]! max-w-[103.55px] md:h-[2px]!"
      />
      <ListWithShowMore
        showMore={showMore}
        onShowMore={handleShowMore}
        containerClassName=""
        buttonContainerClassName="p-4 pt-0"
        paginationButtonClassName={STOCK_LISTING_PAGINATION_BUTTON_CLASS}
        showMoreText="Show More"
        showLessText="Show Less"
      >
        {tableComponentFn ? tableComponentFn() : null}
      </ListWithShowMore>
    </>
  );
}
